import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BookOpen, HeartHandshake, GraduationCap, Users, Sun } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker, Tag } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { SHOW_GOVERNANCE } from "@/utils/flags";

const QUALITIES = [
  { icon: HeartHandshake, t: "Relational first", d: "An Aunty is someone consistent beside a family — able to yarn, listen, and pick young people up when the day has fallen apart." },
  { icon: Sun, t: "Culturally grounded", d: "Grounded in Aboriginal ways of knowing, being and doing — and respectful of each family's own Nation and cultural authority." },
  { icon: BookOpen, t: "Narrative therapists", d: "Trained in narrative therapy — separating people from problems and seeing knowledge and strength before behaviour." },
  { icon: Users, t: "Life-practical", d: "Comfortable beyond the office: walking, moving, cooking, advocating at school, writing letters, showing up." },
];

const STANDARDS = [
  ["Narrative therapy training", "Formal training in narrative therapy and narrative practice, with ongoing development."],
  ["Youth & community qualifications", "Qualifications in youth work, community services, social work, counselling, education or equivalent lived-and-learned expertise."],
  ["Trauma-informed practice", "Training in trauma-aware, healing-centred engagement with children and families."],
  ["Cultural safety & governance", "Cultural supervision and guidance through Elders and community accountability structures."],
  ["Working with Children Check", "Current WWCC and relevant state-based clearances before working with young people."],
  ["National Police Check", "National criminal history screening as a baseline safeguard."],
  ["First aid & mental health first aid", "Current first aid and youth mental health first aid certification."],
  ["Mandatory reporting training", "Trained in child-safe obligations and mandatory reporting responsibilities."],
  ["Professional & cultural supervision", "Regular clinical supervision and cultural supervision — Aunties are supported too."],
];

const Aunties = () => (
  <div data-testid="aunties-page">
    <PageHero
      kicker="About the Aunties"
      title={"Someone consistent beside them.\nQualified to be there."}
      lede="A Ngawilyi Aunty is a narrative therapist who doesn't stay behind a desk — blending therapy, mentoring, movement and cultural connection in one consistent relationship. Here is who a Ngawilyi Aunty is, and the standard we are building to."
    />

    <section className="px-6 py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <Kicker>What makes a Ngawilyi Aunty</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
            Warm enough to yarn. <span className="italic text-terracotta">Skilled enough to hold the hard days.</span>
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {QUALITIES.map((q, i) => (
            <Reveal key={q.t} delay={i * 0.07}>
              <div data-testid={`aunty-quality-${i}`} className={`rounded-[36px] p-8 h-full transition-transform duration-500 hover:-translate-y-2 ${i === 0 ? "bg-terracotta text-cream" : "bg-creamdark border border-ochre/25 text-charcoal"}`}>
                <q.icon className={`h-8 w-8 ${i === 0 ? "text-cream" : "text-terracotta"}`} strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-2xl">{q.t}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${i === 0 ? "text-cream/80" : "text-earth/85"}`}>{q.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="px-6 py-20 sm:py-28 bg-charcoal text-cream grain relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-terracotta/25 organic-blob blur-3xl" />
      <div className="relative max-w-7xl mx-auto">
        <Reveal className="mb-6">
          <Kicker light>Qualifications & safeguards</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] max-w-3xl">
            The standard every Ngawilyi Aunty <span className="italic text-ochre">will be held to.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mb-12"><Tag tone="ochre">Proposed standard — Ngawilyi is an emerging organisation building its team</Tag></div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {STANDARDS.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.05}>
              <div data-testid={`aunty-standard-${i}`} className="rounded-3xl bg-cream/[0.06] border border-cream/12 p-7 h-full backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-ochre shrink-0" strokeWidth={1.75} />
                  <h3 className="font-semibold text-cream">{t}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-cream/70">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="px-6 py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
        <Reveal>
          <Kicker>Two kinds of knowledge</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Professional knowledge. <span className="italic text-terracotta">Lived and cultural knowledge.</span>
          </h2>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
            Qualifications matter — and so does the knowledge that comes from community, culture and lived
            experience. Ngawilyi Aunties bring both: the training to work safely and well, and the relational,
            cultural and life knowledge that no certificate can teach.
          </p>
          <p className="mt-5 text-base lg:text-lg leading-relaxed text-earth">
            Aunties are supported by narrative supervision, cultural governance through Elders, and a model that
            values their wellbeing too — you can't walk beside families well if no one walks beside you.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-[40px] bg-blush/40 border border-blush p-10 sm:p-12 grain relative overflow-hidden">
            <div className="absolute -bottom-16 -right-16 w-56 h-56 bg-terracotta/15 organic-blob blur-2xl" />
            <div className="relative">
              <GraduationCap className="h-8 w-8 text-terracotta" strokeWidth={1.5} />
              <h3 className="mt-6 font-display text-3xl text-charcoal">Could you be a Ngawilyi Aunty?</h3>
              <p className="mt-4 text-earth/85 leading-relaxed">
                Our team is being built through community consultation. If you are an Aboriginal practitioner,
                youth worker, narrative therapist, mentor, carer or community member with knowledge to offer —
                we would love to yarn with you about what's coming.
              </p>
              <Link data-testid="aunties-eoi-btn" to="/consultation" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
                Express Interest <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <p className="mt-6 text-xs text-earth/60 leading-relaxed">
                Expressions of interest are not job offers — they are the start of a conversation about how this
                team should be built.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* CULTURAL GOVERNANCE — preview only until committee content is confirmed */}
    {SHOW_GOVERNANCE && (
    <section className="px-6 py-20 sm:py-28 bg-cream border-t border-ochre/15">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Kicker>Cultural governance</Kicker>
          <h2 className="mt-4 font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
            Our Cultural Governance Committee
          </h2>
          <p className="mt-7 text-base lg:text-lg leading-relaxed text-earth max-w-2xl">
            Ngawilyi Narrative is establishing a Cultural Governance Committee to bring cultural guidance,
            community perspectives and shared reflection into the organisation's development — keeping culture,
            relationships and community priorities central as Ngawilyi grows.
          </p>
          <Link data-testid="aunties-governance-link" to="/cultural-governance" className="group mt-9 inline-flex items-center gap-2 rounded-full border-2 border-charcoal px-8 py-[14px] text-sm font-bold uppercase tracking-widest text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-cream">
            About the Committee <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
    )}

    <CTABand
      title="The right person beside a child can change what a hard day becomes."
      text="Refer a young person, partner on the model, or help us shape the team."
      primary={{ to: "/referrals", label: "Make a Referral" }}
      secondary={{ to: "/partner", label: "Partner With Ngawilyi" }}
    />
  </div>
);

export default Aunties;
